import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import {LoadingButton} from '@mui/lab';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  IconButton,
  TextField,
} from '@mui/material';
import {LocalizationProvider, TimePicker} from '@mui/x-date-pickers';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, {Dayjs} from 'dayjs';
import {useFormik} from 'formik';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import {useSnackbar} from 'notistack';
import {useEffect, useState} from 'react';
import * as yup from 'yup';

import API, {getMessagesFromApiError} from '../../api/axios';
import {apiBaseUrl} from '../../api/urls';
import {useAppDispatch} from '../../hooks/redux';
import {Shift, ShiftUpsertInputBody} from '../../interfaces/Shift';
import reduxActions from '../../redux/actions';
import {timeValidator} from '../../scheme/yup/utils';
import {CloseSnackbarAction} from '../common/CloseSnackbarButton';
import SnackbarMessages from '../common/SnackbarMessages';
import StatusSelect from '../selectors/StatusSelect';

interface Props {
  pk?: number;
  item?: Shift;
  prefetch?: boolean;
  onClose?: () => void;
  onSubmitted?: (item: Shift) => void;
}

const ShiftItemUpsert = ({pk, item, prefetch, onClose, onSubmitted}: Props) => {
  /*********/
  /* fetch */
  /*********/

  const [fetchedData, setFetchedData] = useState(cloneDeep(item));
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);

  const fetchData = async () => {
    setFetchedInProgress(true);
    try {
      const resp = await API.get<Shift>(`${apiBaseUrl}/shift/${pk}`);
      setFetchedData(resp.data);
      formik.setValues(getFormikValues(resp.data));
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, "STRA_ae66ffb4");
      setFetchedErrors(messages);
    }
    setFetchedInProgress(false);
  };

  useEffect(() => {
    if (prefetch) {
      fetchData();
    }
  }, [pk, prefetch]);

  useEffect(() => {
    if (!isEqual(item, fetchedData)) {
      setFetchedData(item);
      formik.setValues(getFormikValues(item));
    }
  }, [item]);

  /**********/
  /* submit */
  /**********/

  const {enqueueSnackbar} = useSnackbar();
  const [submittedInProgress, setSubmittedInProgress] = useState(false);
  const reduxDispatch = useAppDispatch();

  const submitData = async (data: ShiftUpsertInputBody) => {
    setSubmittedInProgress(true);
    try {
      const endpoint = pk ? `${apiBaseUrl}/shift/${pk}` : `${apiBaseUrl}/shift`;
      const resp = pk
        ? await API.patch<Shift>(endpoint, data)
        : await API.post<Shift>(endpoint, data);
      const message = `Shift successfully ${pk ? 'updated' : 'created'}`;
      enqueueSnackbar(message, {
        variant: 'success',
        action: CloseSnackbarAction,
      });

      reduxDispatch(reduxActions.assets.fetchShifts);
      onSubmitted?.(resp.data);
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, "STRA_4f720ddd");
      enqueueSnackbar(<SnackbarMessages messages={messages} />, {
        variant: 'error',
        action: CloseSnackbarAction,
      });
    }
    setSubmittedInProgress(false);
  };

  /*********/
  /* input */
  /*********/
  const inputValidationSchema = yup.object().shape({
    name: yup.string().nullable().required('Field is required'),
    status: yup.string().nullable().required('Field is required'),
    hour_start: yup
      .string()
      .nullable()
      .matches(timeValidator.regex, timeValidator.message)
      .required('Field is required'),
    hour_end: yup
      .string()
      .nullable()
      .matches(timeValidator.regex, timeValidator.message)
      .required('Field is required'),
    description: yup.string().nullable(),
    priority: yup.number().nullable(),
  });

  const getFormikValues = (item?: Shift): ShiftUpsertInputBody => ({
    name: item?.name ?? null,
    status: item?.status ?? null,
    description: item?.description ?? null,
    hour_start: item?.hour_start ?? null,
    hour_end: item?.hour_end ?? null,
    priority: item?.priority ?? null,
  });

  const formik = useFormik<ShiftUpsertInputBody>({
    initialValues: getFormikValues(fetchedData),
    validationSchema: inputValidationSchema,
    onSubmit: async (values) => {
      await submitData(values);
    },
  });

  useEffect(() => {
    const newInput = getFormikValues(fetchedData);
    if (!isEqual(formik.values, newInput)) {
      formik.setValues(newInput);
    }
  }, [fetchedData]);

  const ActionIcon = pk ? EditIcon : AddIcon;
  const actionName = pk ? 'Update' : 'Create';

  return (
    <Box
      component="form"
      display="flex"
      flexDirection="column"
      position="relative"
      gap={3}
      p={3}
      onSubmit={formik.handleSubmit}
    >
      <Box display="flex" justifyContent="space-between">
        <Box display="flex" alignItems="center" width="100%" gap={1.5}>
          <ActionIcon sx={{color: 'primary.main'}} />
          <Box fontSize={24}>{actionName} Shift</Box>
        </Box>
        {onClose ? (
          <IconButton onClick={() => onClose()}>
            <CloseIcon />
          </IconButton>
        ) : null}
      </Box>
      <Backdrop open={fetchedInProgress} sx={{position: 'absolute'}}>
        <CircularProgress color="inherit" />
      </Backdrop>
      {fetchedErrors.map((error, index) => (
        <Alert key={index} severity="error">
          {error}{' '}
        </Alert>
      ))}
      <Box display="flex" flexDirection="column" gap={3}>
        <TextField
          value={formik.values.name ?? ''}
          label="Name"
          size="small"
          name="name"
          fullWidth
          error={!!formik.touched.name && !!formik.errors.name}
          helperText={formik.touched.name && formik.errors.name}
          onChange={formik.handleChange}
        />
        <StatusSelect
          value={formik.values.status}
          fullWidth
          name="status"
          label="Status"
          size="small"
          select
          error={!!formik.touched.status && !!formik.errors.status}
          helperText={formik.touched.status && formik.errors.status}
          onChange={formik.handleChange}
        />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <TimePicker
            label="Hour Start"
            value={
              formik.values.hour_start
                ? dayjs(formik.values.hour_start, 'HH:mm:00')
                : null
            }
            slotProps={{
              textField: {
                fullWidth: true,
                size: 'small',
                error:
                  !!formik.touched.hour_start && !!formik.errors.hour_start,
                helperText:
                  formik.touched.hour_start && formik.errors.hour_start,
              },
            }}
            onChange={(e: Dayjs | null) =>
              formik.setFieldValue('hour_start', e?.format('HH:mm:00'))
            }
          />
        </LocalizationProvider>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <TimePicker
            label="Hour End"
            value={
              formik.values.hour_end
                ? dayjs(formik.values.hour_end, 'HH:mm:00')
                : null
            }
            slotProps={{
              textField: {
                fullWidth: true,
                size: 'small',
                error: !!formik.touched.hour_end && !!formik.errors.hour_end,
                helperText: formik.touched.hour_end && formik.errors.hour_end,
              },
            }}
            onChange={(e: Dayjs | null) =>
              formik.setFieldValue('hour_end', e?.format('HH:mm:ss'))
            }
          />
        </LocalizationProvider>
        <TextField
          value={formik.values.description ?? ''}
          label="Description"
          size="small"
          name="description"
          fullWidth
          multiline
          rows={5}
          error={!!formik.touched.description && !!formik.errors.description}
          helperText={formik.touched.description && formik.errors.description}
          onChange={formik.handleChange}
        />
        <TextField
          value={formik.values.priority ?? ''}
          label="Priority"
          size="small"
          name="priority"
          fullWidth
          type="number"
          error={!!formik.touched.priority && !!formik.errors.priority}
          helperText={formik.touched.priority && formik.errors.priority}
          onChange={formik.handleChange}
        />
      </Box>
      <Box display="flex" justifyContent="end" gap={0.5}>
        {onClose ? <Button onClick={() => onClose()}>Cancel</Button> : null}
        <Box>
          <LoadingButton
            variant="contained"
            type="submit"
            loading={submittedInProgress}
            sx={{ml: 1}}
          >
            {actionName}
          </LoadingButton>
        </Box>
      </Box>
    </Box>
  );
};

export default ShiftItemUpsert;
